import { cond, and, always, T } from 'ramda'

import { isLocal, isDev, isQA, isProd } from '../environments'
import { buildAppHost } from './utils'

// This is likely outdated.
// set useLocalApi to true when you want to swarm with a
// backend dev. They will need to serve the appropriate
// micro services from their local machine. This avoids
// needing to deploy to test the changes.
//
// ** THIS SHOULD ALWAYS BE FALSE WHEN COMMITTING **
export const useLocalApi = false

// this IP should be the local network IP of the computer
// where the API is being served from. Have the backend dev
// that you are swarming with run `ifconfig` on their machine
// and give you their network IP.
const localApiIp = 'localhost:8080'

// normally, we'd be running local apps against dev-api, but sometimes
// we want to run our local app with another env's data so that's
// where this value is useful. Must restart dev server to see changes.
const apiEnv = process.env.APP_ENV || 'dev'

// This allows you to run only the ones you care about
// Often times, you run backend services via local-dev profile to point them to dev
const localServices = process.env.LOCAL_TEST_APIS?.split(',') || []

// an array of conditions that build api hosts for a given env,
// to be used with R.cond(). Used for both member and admin apps.
const apiHostConditions = [
  [isDev, buildAppHost('dev-api')],
  [isQA, buildAppHost('qa-api')],
  [isProd, buildAppHost('prod-api')],
  // This is likely outdated.
  // this line is only for when we want to target a
  // specific service on our network from our localhost.
  // see above where we set the values for more info.
  // and() expects functions, isLocal is already a function
  [and(isLocal, () => useLocalApi), always(`//${localApiIp}`)],
  // this will be used when we're at localhost and trying to
  // point to dev or some other env. Change this value to use
  // your localhost but with another environment API.
  [T, buildAppHost(`${apiEnv}-api`)],
]

// This is the host to use for all API requests in each env.
export const getBaseApiHost = cond(apiHostConditions)
export const BASE_API_HOST = getBaseApiHost(window.location.host)

// This makes complete API endpoints based on a service.
// Where `service` is "auth" and `endpoint` is "location",
// it should generate something like:
//
// https://dev-api.sidecarhealth.com/auth/v1/location
//

export default (service, endpoint, version = 'v1') => {
  const host = _localServiceHost[service]
    ? _localServiceHost[service]
    : BASE_API_HOST
  return `${host}/${service && `${service}/`}${version}/${endpoint}`
}

export const apiVersionPortMapping = {
  v1: {
    file: 5000,
    auth: 8081,
    acct: 8082,
    exp: 8083,
    ntc: 8084,
    care: 8085,
    doc: 8086,
    admin: 8087,
    pricing: 8088,
    ptnr: 8089,
  },
  v2: {
    doc: 8090,
  },
}

// getAPIServerPort :: (String service, String version) -> Number port
export const getAPIServerPort = (service, version = 'v1') => {
  try {
    const port = apiVersionPortMapping[String(version)][service]
    return port
  } catch (error) {
    return 8080
  }
}

const _localServiceHost = localServices.reduce((map, service) => {
  map[service] = `http://127.0.0.1:${getAPIServerPort(service)}`
  return map
}, {})
